import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBriefcase, FiUser, FiMail, FiLock, FiArrowLeft, FiCheck, FiBuilding, FiPhone, FiX, FiShield, FiArrowRight, FiAlertTriangle } = FiIcons;

const PartnerRegistration = () => {
  const navigate = useNavigate();
  const { addPartner } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('partner_registration');

  const [formData, setFormData] = useState({
    name: '',
    orgNr: '',
    contactPerson: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    reason: '',
    termsAccepted: false
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (!formData.name || !formData.email || !formData.password) {
      setError('Vennligst fyll ut alle obligatoriske felt (*)');
      setIsSubmitting(false);
      return;
    }

    if (!formData.termsAccepted) {
      setError('Du må godta vilkårene for å fortsette.');
      setIsSubmitting(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passordene er ikke like.');
      setIsSubmitting(false);
      return;
    }

    if (!validateSubmission()) {
      setIsSubmitting(false);
      return;
    }

    if (typeof addPartner !== 'function') {
      setError('Systemfeil: Kunne ikke koble til databasen (addPartner mangler).');
      setIsSubmitting(false);
      return;
    }

    try {
      const partnerPayload = {
        partner_id: `PARTNER-${Math.floor(1000 + Math.random() * 9000)}`,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        org_nr: formData.orgNr,
        contact_person: formData.contactPerson,
        reason: formData.reason,
        active: false
      };

      const result = await addPartner(partnerPayload);
      if (!result?.success) {
        setError('Partnersøknaden kunne ikke sendes. Prøv igjen.');
        return;
      }
      setSuccess(true);
    } catch (err) {
      console.error("Registration error:", err);
      setError('Partnersøknaden kunne ikke sendes. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F0F4FF] flex items-center justify-center p-4 font-sans text-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-lg w-full border border-blue-50">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <SafeIcon icon={FiCheck} className="text-white text-4xl" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 tracking-tight">Søknad mottatt!</h2>
          <p className="text-gray-600 mb-8 text-base md:text-lg">Takk for din interesse. Vi vil gå gjennom søknaden din og aktivere portalen din innen kort tid.</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold hover:bg-blue-700 transition-all w-full shadow-lg">
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4FF] flex items-center justify-center py-12 px-4 font-sans">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="p-6 md:p-12">
          <div className="text-left mb-8">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-blue-600 text-sm font-bold flex items-center gap-1 mb-6 transition-colors">
              <SafeIcon icon={FiArrowLeft} /> Tilbake
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-xl">
                <SafeIcon icon={FiBriefcase} className="text-white text-3xl" />
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Bli Partner</h1>
              <p className="text-gray-500 font-medium mt-1">Kom i gang på 2 minutter</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <HoneypotField />
            
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm font-bold border border-red-100 flex items-center gap-2 animate-pulse">
                <SafeIcon icon={FiAlertTriangle} /> {error}
              </div>
            )}

            <div>
              <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-blue-50 pb-2">
                <SafeIcon icon={FiBuilding} /> Bedriftsinformasjon
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Firmanavn *</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Partner AS" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Org.nr</label>
                  <input type="text" value={formData.orgNr} onChange={e => setFormData({ ...formData, orgNr: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="999 888 777" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-blue-50 pb-2">
                <SafeIcon icon={FiUser} /> Kontaktperson & Innlogging
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Fullt navn *</label>
                    <input type="text" required value={formData.contactPerson} onChange={e => setFormData({ ...formData, contactPerson: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Navn Navnesen" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Telefon *</label>
                    <div className="relative">
                      <SafeIcon icon={FiPhone} className="absolute left-3 top-3.5 text-gray-400" />
                      <input type="tel" required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="900 00 000" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">E-post (Brukernavn) *</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 text-gray-400" />
                    <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold text-gray-900" placeholder="admin@hmsvarsling.no" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Passord *</label>
                    <input type="password" required value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1 ml-1">Bekreft Passord *</label>
                    <input type="password" required value={formData.confirmPassword} onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-blue-50 pb-2">
                <SafeIcon icon={FiBriefcase} /> Hvorfor vil du bli partner?
              </h3>
              <textarea
                value={formData.reason}
                onChange={e => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                placeholder="Fortell oss kort om din bedrift og hvorfor dere ønsker å bli partner med HMSVarsling..."
              ></textarea>
            </div>

            <div className="pt-4">
              <div className="bg-blue-50/50 p-4 rounded-xl mb-6 border border-blue-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.termsAccepted} onChange={e => setFormData({ ...formData, termsAccepted: e.target.checked })} className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all" />
                  <span className="text-xs md:text-sm text-gray-700">
                    Jeg godtar <button type="button" onClick={() => setShowTermsModal(true)} className="text-blue-600 hover:underline font-bold">vilkårene for partnerprogrammet</button>
                  </span>
                </label>
              </div>

              <button type="submit" disabled={isSubmitting} className={`w-full bg-blue-600 text-white py-4 md:py-5 rounded-2xl font-black text-base md:text-lg hover:bg-blue-700 shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {isSubmitting ? 'Sender...' : 'Send partner-søknad'}
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* VILKÅR MODAL */}
      <AnimatePresence>
        {showTermsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowTermsModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl max-h-[80vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-blue-50 text-blue-900">
                <h2 className="text-xl font-bold flex items-center gap-2"><SafeIcon icon={FiBriefcase} /> Partneravtale Vilkår</h2>
                <button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-blue-100 rounded-full text-blue-900"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-8 overflow-y-auto prose prose-sm max-w-none text-gray-700 space-y-6">
                <section>
                  <h3 className="font-bold text-gray-900">1. Partnerrollen</h3>
                  <p>Som partner forplikter du deg til å opptre profesjonelt og i tråd med HMSVarsling sine verdier.</p>
                </section>
                <section>
                  <h3 className="font-bold text-gray-900">2. Provisjon</h3>
                  <p>Partner mottar provisjon i henhold til gjeldende satser. Utbetaling skjer kvartalsvis.</p>
                </section>
                <section>
                  <h3 className="font-bold text-gray-900">3. Konfidensialitet</h3>
                  <p>Partner har taushetsplikt om forretningshemmeligheter og kundeinformasjon.</p>
                </section>
              </div>
              <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button onClick={() => { setFormData({ ...formData, termsAccepted: true }); setShowTermsModal(false); }} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors">
                  Jeg godtar vilkårene
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerRegistration;
